import type { SeasonRate } from '@/types/profile';

/**
 * G6 — seasonal rates (docs/g6-pricing-survey-2026-11-02.md §2.1).
 *
 * A season says "between from and to, a night costs rate instead of the flat
 * nightly rate". Dates are inclusive calendar days (store as 'YYYY-MM-DD'),
 * so the host's "1 Dec – 10 Jan" includes Christmas and New Year both ways.
 *
 * Three honesty rules, each pinned by tests:
 * 1. Seasons only ever make things CHEAPER-OR-DIFFERENT on their dates — when
 *    no season matches a night, the host's flat nightly rate applies
 *    (behaviour identical to pre-G6).
 * 2. The engine still prices a stay as ONE nightly rate × nights (the quote
 *    the checkout snapshot locks is a single rate). A stay that crosses a
 *    season boundary is priced at the AVERAGE of what its nights would cost
 *    night-by-night — same total, quotient form it is one.
 * 3. Earlier seasons win on overlap (sorted by `from`); no surprise rule.
 */

export type { SeasonRate } from '@/types/profile';

const normalizeDateOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const asDate = new Date(`${value.trim()}T12:00:00Z`);
  if (!Number.isFinite(asDate.getTime()) || asDate.toISOString().slice(0, 10) !== value.trim()) {
    return null;
  }
  return value.trim();
};

const sanitizeRate = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null;
};

/** Drop junk rows, repair nothing silently — a season that cannot be trusted is a season that does not exist. */
export const coerceSeasonalRates = (raw: unknown): SeasonRate[] => {
  if (!Array.isArray(raw)) return [];
  const rows: SeasonRate[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const candidate = entry as Record<string, unknown>;
    const from = normalizeDateOrNull(candidate.from);
    const to = normalizeDateOrNull(candidate.to);
    const rate = sanitizeRate(candidate.rate);
    if (!from || !to || from > to || rate === null) continue;
    rows.push({
      id: typeof candidate.id === 'string' && candidate.id.trim() ? candidate.id.trim().slice(0, 64) : `season-${from}-${to}`,
      label: typeof candidate.label === 'string' && candidate.label.trim()
        ? candidate.label.trim().slice(0, 60)
        : `${from} → ${to}`,
      from,
      to,
      rate,
    });
  }
  return rows.sort((a, b) => a.from.localeCompare(b.from));
};

/** Nightly rate for ONE calendar night: the earliest matching season, else null (caller falls back to the flat rate). */
export const seasonalRateForNight = (rates: SeasonRate[], date: Date): number | null => {
  const dayKey = date.toISOString().slice(0, 10);
  for (const season of rates) {
    if (season.from <= dayKey && dayKey <= season.to) return season.rate;
  }
  return null;
};

export const STAY_NIGHT_MS = 86_400_000;

export type SeasonalWindowQuote = {
  /** AVERAGE nightly rate over the window (per-night total ÷ nights) — the one rate the snapshot locks. */
  averageNightlyRate: number;
  nights: number;
  /** Per-night table the UI can show instead of an opaque quotient. */
  nightlyRates: number[];
  cheapestNight: number;
  dearestNight: number;
};

/**
 * The whole-stay rate: every occupied night reads its own season (else the
 * flat rate), and the window is priced at the exact average of that table —
 * nights × average = the exact per-night total, so the guest pays exactly
 * what a night-by-night break-down says.
 */
export const averageSeasonalNightlyRate = (opts: {
  rates: SeasonRate[] | null | undefined;
  fallbackRate: number;
  startDate: Date;
  endDate: Date;
}): SeasonalWindowQuote => {
  const { rates, fallbackRate, startDate, endDate } = opts;
  const nights = Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / STAY_NIGHT_MS));
  const nightlyRates: number[] = [];
  for (let i = 0; i < nights; i += 1) {
    const nightDate = new Date(startDate.getTime() + i * STAY_NIGHT_MS);
    nightlyRates.push(seasonalRateForNight(rates ?? [], nightDate) ?? fallbackRate);
  }
  const total = nightlyRates.reduce((sum, rate) => sum + rate, 0);
  return {
    averageNightlyRate: Math.round((total / nights) * 100) / 100,
    nights,
    nightlyRates,
    cheapestNight: Math.min(...nightlyRates),
    dearestNight: Math.max(...nightlyRates),
  };
};

/**
 * Did any night actually match a season? A season rate coincidentally equal
 * to the flat rate IS "no seasonal effect" — resolve the flat path then, and
 * the quote is byte-identical to pre-G6.
 */
export const windowTouchesSeason = (quote: SeasonalWindowQuote, fallbackRate: number): boolean =>
  quote.nightlyRates.some((rate) => rate !== fallbackRate);

/**
 * Guest-facing summary before dates are chosen: the cheapest and dearest
 * nightly rates across the year INCLUDING the flat rate as a band, so a
 * listing can say "R400 quiet season · R900 at peak". Null when there are no
 * seasons — the flat band alone needs no summary.
 */
export const seasonalPriceSpan = (
  rates: SeasonRate[] | null | undefined,
  baseRate: number,
): { from: number; to: number } | null => {
  const rows = coerceSeasonalRates(rates);
  if (!rows.length || !(baseRate > 0)) return null;
  const all = [...rows.map((row) => row.rate), baseRate];
  return { from: Math.min(...all), to: Math.max(...all) };
};
